/**
 * One row, as a card.
 *
 * What a gallery lays out in a grid and a board stacks in a column, so it is
 * written once. A card is the primary field as a title and every other visible
 * field under it as a labelled value, optionally under a cover picture -- which
 * is the arrangement both view types use, and the arrangement the MCP App's
 * read-only shells already draw.
 *
 * The values go through `CellDisplay`, the same renderer the grid uses: a single
 * select is its coloured chip here because it is its coloured chip everywhere.
 * Read-only, and not because a card is a lesser surface -- editing a row happens
 * in the row dialog, which is one click away and shows the whole row rather than
 * the part a card has room for.
 */
import type { Field, RowData } from '@/api/types'
import { cn } from '@/shared/lib/utils'
import type { RowColors } from '../decorations/colors'
import type { FieldContext } from '../fields/behavior/behavior'
import { cellValue } from '../fields/read'
import { fileThumbnail, normaliseFiles } from '../fields/files'
import { CellDisplay } from '../fields/ui/read'
import {
  CARD_FIELD_HEIGHT,
  CARD_PADDING,
  CARD_TITLE_HEIGHT,
  COVER_HEIGHT,
} from '../gallery/layout'

export function RowCard({
  row,
  fields,
  coverField,
  context,
  colors,
  onOpen,
}: {
  row: RowData
  /** In the order the view shows them, primary first. */
  fields: readonly Field[]
  coverField: Field | null
  context: FieldContext
  colors?: RowColors
  onOpen?: () => void
}) {
  const [title, ...rest] = fields
  const cover = coverImage(row, coverField)

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`card-${row.id}`}
      // A button rather than a div with a handler, so a card is reachable by
      // keyboard and announced as something that can be opened. The whole card
      // is the target: there is nothing else on it to click.
      className={cn(
        'bg-card text-card-foreground flex h-full w-full flex-col overflow-hidden',
        'rounded-lg border text-left shadow-sm',
        'focus-visible:ring-ring hover:border-ring/40 focus-visible:ring-2 focus-visible:outline-none',
      )}
      style={{
        ...(colors?.background === null || colors?.background === undefined
          ? {}
          : { backgroundColor: colors.background }),
        ...(colors?.leftBorder === null || colors?.leftBorder === undefined
          ? {}
          : { borderLeft: `4px solid ${colors.leftBorder}` }),
      }}
    >
      {cover === null ? null : (
        <img
          src={cover}
          alt=""
          loading="lazy"
          className="bg-muted w-full shrink-0 object-cover"
          style={{ height: COVER_HEIGHT }}
        />
      )}
      <div className="min-w-0 flex-1" style={{ padding: CARD_PADDING }}>
        {title === undefined ? null : (
          <div
            className="flex items-center truncate text-sm font-semibold"
            style={{ height: CARD_TITLE_HEIGHT }}
          >
            <CellDisplay field={title} row={row} context={context} />
          </div>
        )}
        {rest.map((field) => (
          <div
            key={field.id}
            className="min-w-0 overflow-hidden"
            style={{ height: CARD_FIELD_HEIGHT }}
          >
            <div className="text-muted-foreground truncate text-[11px] uppercase">
              {field.name}
            </div>
            <div className="flex min-w-0 items-center truncate text-sm">
              <CellDisplay field={field} row={row} context={context} />
            </div>
          </div>
        ))}
      </div>
    </button>
  )
}

/**
 * The picture at the top of a card, or nothing.
 *
 * The first file of the cover column that has a `card_cover` thumbnail -- never
 * the file itself, which may be a five-megabyte photograph, and never a smaller
 * thumbnail stretched to fill the space.
 */
function coverImage(row: RowData, coverField: Field | null): string | null {
  if (coverField === null) return null
  for (const file of normaliseFiles(cellValue(coverField, row))) {
    const url = fileThumbnail(file, 'card_cover')
    if (url !== null) return url
  }
  return null
}
